import React, { useEffect, useRef, useState } from "react";
import { Loader2, Home, UserRound, Database, Check } from "lucide-react";

import { searchProposalSubjects } from "../api/prospectingReportsApi";

const ICONS = { property: Home, lead: UserRound, owner: Database };

/**
 * Property address box for a new proposal that searches what the CRM already
 * holds (CRM properties, seller leads/appraisals, Database owners). Picking a
 * match fills the suburb and vendor details and links the record (CG QA,
 * 26 Oct). Typing over a picked address unlinks it again.
 */
const SubjectAddressSearch = ({ value, onChange, onPick, linked, className }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const search = (q) => {
    clearTimeout(timer.current);
    if ((q || "").trim().length < 3) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(() => {
      const mine = ++seq.current;
      setLoading(true);
      searchProposalSubjects(q.trim())
        .then(({ data }) => { if (mine === seq.current) { setResults(data?.results || []); setOpen(true); } })
        .catch(() => { if (mine === seq.current) setResults([]); })
        .finally(() => { if (mine === seq.current) setLoading(false); });
    }, 250);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input className={className} value={value} placeholder="Start typing an address…"
          onChange={(e) => { onChange(e.target.value); search(e.target.value); }}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)} />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>
      {linked && (
        <p className="mt-1 text-[11px] text-[#2f9e6b] inline-flex items-center gap-1">
          <Check className="w-3 h-3" /> Linked to {linked}; details filled from the CRM.
        </p>
      )}
      {open && results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {results.map((r, i) => {
            const Icon = ICONS[r.source] || Home;
            return (
              <li key={`${r.source}-${i}`}>
                <button type="button" onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onPick(r); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-start gap-2">
                  <Icon className="w-4 h-4 mt-0.5 text-[#0f8d9b] shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{r.full_address}</span>
                    <span className="block text-xs text-gray-500 truncate">
                      {r.label}{r.recipient_name ? ` · ${r.recipient_name}` : ""}{r.recipient_email ? ` · ${r.recipient_email}` : ""}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SubjectAddressSearch;
